import { useState, useCallback } from "react";
import { deepClone } from "../utils";

async function saveServiceViaApi(api, locationId, editData, services, servicesRef, notify) {
  const isUpdate = services.some((service) => service.id === editData.id);
  if (isUpdate) {
    await api.updateServiceAssociation(locationId, editData.id, {
      assignable: editData.assignable,
      automated_provisioning_assn: editData.automatedProvisioningAssn,
      availability_type: editData.availType,
      available: editData.available,
      auto_provision_capable: editData.autoProv,
      external_group: editData.externalGroup,
      external_id: editData.externalId,
      explorable: editData.explorable,
      location_spcfc_url: editData.locationSpcfcUrl,
      sso_enabled: editData.sso,
    });
    notify(`${editData.name} updated`);
    return true;
  }
  const service = (servicesRef || []).find((ref) => ref.name === editData.name);
  if (!service) {
    notify("Service not found; choose a service from the reference list", "error");
    return false;
  }
  await api.createServiceAssociation(locationId, {
    assignable: editData.assignable,
    automated_provisioning_assn: editData.automatedProvisioningAssn,
    availability_type: editData.availType,
    available: editData.available,
    auto_provision_capable: editData.autoProv,
    external_group: editData.externalGroup,
    external_id: editData.externalId,
    explorable: editData.explorable,
    location_spcfc_url: editData.locationSpcfcUrl,
    service_key: service.id,
    sso_enabled: editData.sso,
  });
  notify(`${editData.name} added`);
  return true;
}

async function saveResourceViaApi(api, locationId, editData, resources, resourcesRef, notify) {
  const isUpdate = resources.some((resource) => resource.id === editData.id);
  if (isUpdate) {
    await api.updateResourceAssociation(locationId, editData.id, {
      active: editData.active,
      record_status: editData.recordStatus,
    });
    notify(`${editData.name} updated`);
    return true;
  }
  const resource = (resourcesRef || []).find((ref) => ref.name === editData.name);
  if (!resource) {
    notify("Resource not found; choose a resource from the reference list", "error");
    return false;
  }
  await api.createResourceAssociation(locationId, {
    resource_key: resource.id,
    active: editData.active,
    record_status: editData.recordStatus,
  });
  notify(`${editData.name} added`);
  return true;
}

/**
 * @param {Array} services - Current services (from config when using API).
 * @param {Function} setServices - Update local services (no-op when using API).
 * @param {Array} resources - Current resources.
 * @param {Function} setResources - Update local resources (no-op when using API).
 * @param {Function} notify - (message, type?) => void
 * @param {{ api: object, locationId: string, refetchConfig: () => void, servicesRef: Array, resourcesRef: Array }} [apiOpts] - When provided, save/delete call API then refetchConfig.
 */
export function useLocationConfigCrud(services, setServices, resources, setResources, notify, apiOpts) {
  const [editId, setEditId] = useState(null);
  const [editType, setEditType] = useState(null);
  const [editData, setEditData] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [crudBusy, setCrudBusy] = useState(false);

  const startEdit = useCallback((item, type) => {
    setEditId(item.id);
    setEditType(type);
    setEditData(deepClone(item));
    setDeleteConfirmation(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditId(null);
    setEditType(null);
    setEditData(null);
  }, []);

  const saveEdit = useCallback(async () => {
    if (apiOpts?.api && apiOpts?.locationId && apiOpts?.refetchConfig) {
      const { api, locationId, refetchConfig, servicesRef, resourcesRef } = apiOpts;
      setCrudBusy(true);
      try {
        const saveFn = editType === "svc" ? saveServiceViaApi : saveResourceViaApi;
        const items = editType === "svc" ? services : resources;
        const refs = editType === "svc" ? servicesRef : resourcesRef;
        const ok = await saveFn(api, locationId, editData, items, refs, notify);
        if (!ok) return;
        refetchConfig();
        setEditId(null);
        setEditType(null);
        setEditData(null);
      } catch (err) {
        notify(err?.message || "Save failed", "error");
      } finally {
        setCrudBusy(false);
      }
      return;
    }
    if (editType === "svc") {
      setServices((prev) => prev.map((service) => (service.id === editData.id ? { ...editData } : service)));
    } else {
      setResources((prev) => prev.map((resource) => (resource.id === editData.id ? { ...editData } : resource)));
    }
    notify(`${editData.name} updated`);
    setEditId(null);
    setEditType(null);
    setEditData(null);
  }, [
    editType,
    editData,
    services,
    resources,
    setServices,
    setResources,
    notify,
    apiOpts,
  ]);

  const doDelete = useCallback(
    async (id, type) => {
      const item = type === "svc" ? services.find((service) => service.id === id) : resources.find((resource) => resource.id === id);
      if (apiOpts?.api && apiOpts?.locationId && apiOpts?.refetchConfig) {
        setCrudBusy(true);
        try {
          if (type === "svc") {
            await apiOpts.api.deleteServiceAssociation(apiOpts.locationId, id);
          } else {
            await apiOpts.api.deleteResourceAssociation(apiOpts.locationId, id);
          }
          notify(`${item?.name} removed`);
          apiOpts.refetchConfig();
        } catch (err) {
          notify(err?.message || "Delete failed", "error");
        } finally {
          setCrudBusy(false);
        }
        setDeleteConfirmation(null);
        setEditId(null);
        setEditType(null);
        setEditData(null);
        return;
      }
      if (type === "svc") {
        setServices((prev) => prev.filter((service) => service.id !== id));
      } else {
        setResources((prev) => prev.filter((resource) => resource.id !== id));
      }
      notify(`${item?.name} removed`);
      setDeleteConfirmation(null);
      setEditId(null);
      setEditType(null);
      setEditData(null);
    },
    [services, resources, setServices, setResources, notify, apiOpts]
  );

  return {
    editId,
    editType,
    editData,
    setEditData,
    deleteConfirmation,
    setDeleteConfirmation,
    startEdit,
    cancelEdit,
    saveEdit,
    doDelete,
    crudBusy,
  };
}
